/**
 * FilterPostsModal — web counterpart of mobile's PostFilter screen.
 *
 * Two tabs:
 *  - Filter By People: see-all toggle, gender, buddy age, current weight
 *    (with kg/lbs unit toggle), wants-to-lose (with kg/lbs + "Any"), country,
 *    following / followers switches, clear + apply buttons.
 *  - Filter By Hashtag: search input, suggestion list, selected hashtag chips,
 *    clear + apply buttons.
 *
 * Filter state is persisted via the shared storage abstraction so the same
 * selection is restored when the user reopens Filter Posts — mirroring mobile.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useFeed } from '@wlb/shared/context/FeedContext';
import { storage } from '@wlb/shared/platform/storage';
import { STORAGE_KEYS } from '@wlb/shared/constants';
import { COUNTRIES } from '@wlb/shared/constants/countries';
import { kgToLbs, lbsToKg } from '@wlb/shared/utils/weightUtils';
import type { FeedFilterParams } from '@wlb/shared/types/feed';
import { Button } from '../../components/ui/Button';
import { ScaleBar } from '../../components/ui/ScaleBar';
import { Modal } from '../../components/ui/Modal';
import { CloseIcon, SearchIcon } from '../../components/ui/icons';
import './feedModal.css';

/* ─── Constants (mirror mobile) ──────────────────────────────────────── */

const AGE_MIN = 18;
const AGE_MAX = 75;
const WEIGHT_MIN_KG = 36;
const WEIGHT_MAX_KG = 127;
const LOSE_MIN_KG = 2;
const LOSE_MAX_KG = 27;

const ALL_HASHTAGS = [
  '#weightloss', '#fitness', '#healthyfood', '#workout',
  '#fatloss', '#diet', '#running', '#transformation',
];

const GENDERS = ['All', 'Male', 'Female', 'Other'] as const;

const scaleTicks = (min: number, max: number, count: number): string[] =>
  Array.from({ length: count }, (_, i) =>
    String(Math.round(min + ((max - min) * i) / (count - 1))),
  );

/* ─── Saved state shape (mirror mobile's SavedPeopleFilter) ──────────── */

interface SavedPeopleFilter {
  seeAll: boolean;
  gender: string;
  buddyAge: { min: number; max: number };
  weightUnit: string;
  currentWeightKg: { min: number; max: number };
  loseUnit: string;
  wantsToLoseKg: number;
  country?: number;
  following: boolean;
  followers: boolean;
}

/* ─── Props ──────────────────────────────────────────────────────────── */

interface FilterPostsModalProps {
  onClose: () => void;
}

/* ─── Component ──────────────────────────────────────────────────────── */

export function FilterPostsModal({ onClose }: FilterPostsModalProps) {
  const { t } = useTranslation();
  const { applyFilterAndSearch, clearFilter, activeFilters, searchHashtag, setTempHashtagFilter } = useFeed();
  const [tab, setTab] = useState<'people' | 'hashtags'>('people');

  /* ── People tab state ──────────────────────────────────────────────── */
  const [seeAll, setSeeAll] = useState(activeFilters?.isAllPost ?? false);
  const [gender, setGender] = useState<string>('All');
  const [buddyAge, setBuddyAge] = useState({
    min: activeFilters?.ageFrom ?? 25,
    max: activeFilters?.ageTo ?? 45,
  });
  const [weightUnit, setWeightUnit] = useState('lbs');
  const [currentWeightKg, setCurrentWeightKg] = useState({
    min: activeFilters?.minWeight ?? 78,
    max: activeFilters?.maxWeight ?? 111,
  });
  const [loseUnit, setLoseUnit] = useState('lbs');
  const [wantsToLoseKg, setWantsToLoseKg] = useState(activeFilters?.wantToLose ?? -1);
  const [country, setCountry] = useState<number | undefined>(undefined);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  /* ── Hashtag tab state ─────────────────────────────────────────────── */
  const [hashtagSearch, setHashtagSearch] = useState('');
  const [selectedHashtags, setSelectedHashtags] = useState<string[]>([]);

  /* ── Restore saved people filter on mount ──────────────────────────── */
  useEffect(() => {
    storage.getItem<SavedPeopleFilter | null>(STORAGE_KEYS.POST_FILTER_PEOPLE, null)
      .then(saved => {
        if (!saved) return;
        setSeeAll(saved.seeAll);
        setGender(saved.gender ?? 'All');
        setBuddyAge(saved.buddyAge ?? { min: 25, max: 45 });
        setWeightUnit(saved.weightUnit ?? 'lbs');
        setCurrentWeightKg(saved.currentWeightKg ?? { min: 78, max: 111 });
        setLoseUnit(saved.loseUnit ?? 'lbs');
        setWantsToLoseKg(saved.wantsToLoseKg ?? -1);
        setCountry(saved.country);
        setFollowing(saved.following ?? false);
        setFollowers(saved.followers ?? false);
      })
      .catch(() => undefined);
  }, []);

  /* ── Restore saved hashtags on mount ───────────────────────────────── */
  useEffect(() => {
    storage.getItem<string[]>(STORAGE_KEYS.POST_FILTER_HASHTAGS, [])
      .then(saved => {
        if (Array.isArray(saved) && saved.length > 0) setSelectedHashtags(saved);
      })
      .catch(() => undefined);
  }, []);

  /* ── Derived display values ────────────────────────────────────────── */
  const isLbsWeight = weightUnit === 'lbs';
  const isLbsLose = loseUnit === 'lbs';

  const weightConfig = useMemo(() => ({
    min: isLbsWeight ? kgToLbs(WEIGHT_MIN_KG) : WEIGHT_MIN_KG,
    max: isLbsWeight ? kgToLbs(WEIGHT_MAX_KG) : WEIGHT_MAX_KG,
  }), [isLbsWeight]);

  const loseConfig = useMemo(() => ({
    min: isLbsLose ? kgToLbs(LOSE_MIN_KG) : LOSE_MIN_KG,
    max: isLbsLose ? kgToLbs(LOSE_MAX_KG) : LOSE_MAX_KG,
  }), [isLbsLose]);

  const ageTicks = useMemo(() => scaleTicks(AGE_MIN, AGE_MAX, 7), []);
  const weightTicks = useMemo(() => scaleTicks(weightConfig.min, weightConfig.max, 6), [weightConfig]);
  const loseTicks = useMemo(() => scaleTicks(loseConfig.min, loseConfig.max, 7), [loseConfig]);

  const currentWeightDisplay = useMemo(() => ({
    min: isLbsWeight ? kgToLbs(currentWeightKg.min) : currentWeightKg.min,
    max: isLbsWeight ? kgToLbs(currentWeightKg.max) : currentWeightKg.max,
  }), [isLbsWeight, currentWeightKg]);

  const wantsToLoseDisplay = useMemo(() => {
    if (wantsToLoseKg === -1) return -1;
    return isLbsLose ? kgToLbs(wantsToLoseKg) : wantsToLoseKg;
  }, [isLbsLose, wantsToLoseKg]);

  const hashtagSuggestions = useMemo(() => {
    if (!hashtagSearch.trim()) return [];
    const q = hashtagSearch.trim().toLowerCase();
    return ALL_HASHTAGS.filter(h => h.toLowerCase().includes(q) && !selectedHashtags.includes(h));
  }, [hashtagSearch, selectedHashtags]);

  const showPeopleFilters = !seeAll && !following && !followers;

  /* ── Handlers: People tab ──────────────────────────────────────────── */
  const handleSeeAll = useCallback((v: boolean) => {
    setSeeAll(v);
    if (v) {
      setFollowing(false);
      setFollowers(false);
      setGender('All');
      setBuddyAge({ min: 25, max: 45 });
      setCurrentWeightKg({ min: 78, max: 111 });
      setWantsToLoseKg(-1);
      setCountry(undefined);
    }
  }, []);

  const handleWeightChange = useCallback(([mn, mx]: [number, number]) => {
    setCurrentWeightKg({
      min: isLbsWeight ? lbsToKg(mn) : mn,
      max: isLbsWeight ? lbsToKg(mx) : mx,
    });
  }, [isLbsWeight]);

  const handleLoseChange = useCallback((val: number) => {
    setWantsToLoseKg(val === -1 ? -1 : (isLbsLose ? lbsToKg(val) : val));
  }, [isLbsLose]);

  const handleFollowing = useCallback((v: boolean) => {
    setFollowing(v);
    if (v) setFollowers(false);
  }, []);

  const handleFollowers = useCallback((v: boolean) => {
    setFollowers(v);
    if (v) setFollowing(false);
  }, []);

  const handleApplyPeople = useCallback(async () => {
    if (isApplying || isClearing) return;
    setIsApplying(true);
    try {
      let genderId = 0;
      if (gender === 'Male') genderId = 1;
      else if (gender === 'Female') genderId = 2;
      else if (gender === 'Other') genderId = 3;

      let filterOptionVal: string | null = null;
      if (following && followers) filterOptionVal = null;
      else if (following) filterOptionVal = 'Following';
      else if (followers) filterOptionVal = 'Followers';

      const filterPayload: FeedFilterParams = {
        isAllPost: seeAll,
        gender: genderId,
        ageFrom: buddyAge.min,
        ageTo: buddyAge.max,
        minWeight: currentWeightKg.min,
        maxWeight: currentWeightKg.max,
        wantToLose: wantsToLoseKg === -1 ? null : wantsToLoseKg,
        targetScaleUnit: loseUnit === 'lbs' ? 2 : 1,
        weightScaleUnit: weightUnit === 'lbs' ? 2 : 1,
        country: country ?? 0,
        filterOption: filterOptionVal,
      };

      const toSave: SavedPeopleFilter = {
        seeAll, gender, buddyAge, weightUnit, currentWeightKg,
        loseUnit, wantsToLoseKg, country, following, followers,
      };
      await storage.setItem(STORAGE_KEYS.POST_FILTER_PEOPLE, toSave);

      const savedHashtags = await storage.getItem<string[]>(STORAGE_KEYS.POST_FILTER_HASHTAGS, []);
      const searchString = Array.isArray(savedHashtags) && savedHashtags.length > 0
        ? savedHashtags.join(',') : null;

      setTempHashtagFilter(searchString);
      await applyFilterAndSearch(filterPayload, searchString);
      onClose();
    } catch {
      // silent — don't block close
    } finally {
      setIsApplying(false);
    }
  }, [isApplying, isClearing, seeAll, gender, buddyAge, currentWeightKg, wantsToLoseKg, country, following, followers, loseUnit, weightUnit, applyFilterAndSearch, setTempHashtagFilter, onClose]);

  const handleClearPeople = useCallback(async () => {
    if (isApplying || isClearing) return;
    setIsClearing(true);
    try {
      setSeeAll(true);
      setGender('All');
      setBuddyAge({ min: 18, max: 75 });
      setCurrentWeightKg({ min: WEIGHT_MIN_KG, max: WEIGHT_MAX_KG });
      setWantsToLoseKg(-1);
      setCountry(undefined);
      setFollowing(false);
      setFollowers(false);
      await storage.removeItem(STORAGE_KEYS.POST_FILTER_PEOPLE);
      await clearFilter();
      onClose();
    } finally {
      setIsClearing(false);
    }
  }, [isApplying, isClearing, clearFilter, onClose]);

  /* ── Handlers: Hashtag tab ─────────────────────────────────────────── */
  const handleAddHashtag = useCallback((tag: string) => {
    const formatted = tag.startsWith('#') ? tag : `#${tag}`;
    setSelectedHashtags(prev =>
      prev.includes(formatted) ? prev.filter(d => d !== formatted) : [...prev, formatted],
    );
    setHashtagSearch('');
  }, []);

  const handleRemoveHashtag = useCallback((tag: string) => {
    setSelectedHashtags(prev => prev.filter(d => d !== tag));
  }, []);

  const handleApplyHashtags = useCallback(async () => {
    if (isApplying) return;
    setIsApplying(true);
    try {
      const tags = selectedHashtags.length > 0
        ? selectedHashtags
        : hashtagSearch.trim()
          ? [hashtagSearch.trim().startsWith('#') ? hashtagSearch.trim() : `#${hashtagSearch.trim()}`]
          : [];
      const searchString = tags.length > 0 ? tags.join(',') : null;

      if (tags.length > 0) await storage.setItem(STORAGE_KEYS.POST_FILTER_HASHTAGS, tags);
      else await storage.removeItem(STORAGE_KEYS.POST_FILTER_HASHTAGS);

      setTempHashtagFilter(searchString);
      await searchHashtag(searchString);
      onClose();
    } finally {
      setIsApplying(false);
    }
  }, [isApplying, selectedHashtags, hashtagSearch, searchHashtag, setTempHashtagFilter, onClose]);

  const handleClearHashtags = useCallback(async () => {
    if (isApplying) return;
    setIsApplying(true);
    try {
      setSelectedHashtags([]);
      setHashtagSearch('');
      await storage.removeItem(STORAGE_KEYS.POST_FILTER_HASHTAGS);
      setTempHashtagFilter(null);
      await searchHashtag(null);
      onClose();
    } finally {
      setIsApplying(false);
    }
  }, [isApplying, searchHashtag, setTempHashtagFilter, onClose]);

  /* ── Render ────────────────────────────────────────────────────────── */
  return (
    <Modal title={t('home.postFilter.title')} onClose={onClose} labelledBy="filter-posts-modal-title">
      <div className="filter-modal__content">
        {/* Tab selector */}
        <div className="filter-modal__tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'people'} className={tab === 'people' ? 'active' : ''} onClick={() => setTab('people')}>{t('home.postFilter.filterByPeople')}</button>
          <button type="button" role="tab" aria-selected={tab === 'hashtags'} className={tab === 'hashtags' ? 'active' : ''} onClick={() => setTab('hashtags')}>{t('home.postFilter.filterByHashtag')}</button>
        </div>

        {tab === 'hashtags' ? (
          /* ── Hashtag tab ── */
          <div className="filter-modal__hashtag-tab">
            <div className="filter-modal__search-row">
              <SearchIcon size={18} />
              <input
                className="filter-modal__search-input"
                type="text"
                value={hashtagSearch}
                onChange={e => setHashtagSearch(e.target.value)}
                placeholder={t('common.searchHashtags')}
                aria-label={t('common.searchHashtags')}
              />
            </div>

            {hashtagSuggestions.length > 0 && (
              <ul className="filter-modal__suggestions">
                {hashtagSuggestions.map(tag => (
                  <li key={tag}>
                    <button type="button" className="filter-modal__suggestion" onClick={() => handleAddHashtag(tag)}>
                      {tag}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {hashtagSearch.trim().length > 0 && hashtagSuggestions.length === 0 && (
              <p className="filter-modal__empty">{t('home.postFilter.noMatchingHashtags')}</p>
            )}

            {selectedHashtags.length > 0 && (
              <div className="filter-modal__chips-section">
                <strong>{t('home.postFilter.selectedHashtags')}</strong>
                <div className="filter-modal__chips">
                  {selectedHashtags.map(tag => (
                    <span key={tag} className="filter-modal__chip">
                      {tag}
                      <button type="button" onClick={() => handleRemoveHashtag(tag)} aria-label={t('common.buttons.close')}>
                        <CloseIcon size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="filter-modal__footer">
              <Button variant="outline" onClick={() => { handleClearHashtags().catch(() => undefined); }} disabled={isApplying}>{t('home.postFilter.clearFilter')}</Button>
              <Button onClick={() => { handleApplyHashtags().catch(() => undefined); }} disabled={isApplying}>{t('home.postFilter.applyFilter')}</Button>
            </div>
          </div>
        ) : (
          /* ── People tab ── */
          <div className="filter-modal__people-tab">
            {/* See All Posts */}
            <label className="filter-modal__toggle">
              <span>
                <strong>{t('home.postFilter.seeAllPosts')}</strong>
                <small>{t('home.postFilter.fullCommunity')}</small>
              </span>
              <input type="checkbox" checked={seeAll} onChange={e => handleSeeAll(e.target.checked)} />
            </label>

            {/* Filter details — visible only when all three are OFF */}
            {showPeopleFilters && (
              <>
                {/* Gender */}
                <fieldset>
                  <legend>{t('home.postFilter.gender')}</legend>
                  <div className="filter-modal__choices">
                    {GENDERS.map(option => (
                      <button
                        type="button"
                        key={option}
                        className={gender === option ? 'active' : ''}
                        onClick={() => setGender(option)}
                      >
                        {t(`home.postFilter.${option.toLowerCase()}`)}
                      </button>
                    ))}
                  </div>
                </fieldset>

                {/* Buddy Age */}
                <fieldset className="filter-modal__range">
                  <legend>{t('home.postFilter.buddyAge')}</legend>
                  <ScaleBar
                    mode="range"
                    min={AGE_MIN}
                    max={AGE_MAX}
                    value={[buddyAge.min, buddyAge.max]}
                    onChange={([low, high]) => setBuddyAge({ min: low, max: high })}
                    minAriaLabel={`${t('home.postFilter.buddyAge')} ${t('common.minimum')}`}
                    maxAriaLabel={`${t('home.postFilter.buddyAge')} ${t('common.maximum')}`}
                    ticks={ageTicks}
                  />
                </fieldset>

                {/* Current Weight */}
                <fieldset className="filter-modal__range">
                  <legend>{t('home.postFilter.currentWeight')}</legend>
                  <div className="filter-modal__unit-toggle">
                    {(['kg', 'lbs'] as const).map(u => (
                      <button
                        key={u}
                        type="button"
                        className={`filter-modal__unit${weightUnit === u ? ' active' : ''}`}
                        onClick={() => setWeightUnit(u)}
                        aria-pressed={weightUnit === u}
                      >
                        {t(u === 'kg' ? 'basicInfo.unitShortKg' : 'basicInfo.unitShortLbs')}
                      </button>
                    ))}
                  </div>
                  <ScaleBar
                    mode="range"
                    min={weightConfig.min}
                    max={weightConfig.max}
                    value={[currentWeightDisplay.min, currentWeightDisplay.max]}
                    onChange={handleWeightChange}
                    formatValue={v => `${v} ${weightUnit}`}
                    minAriaLabel={`${t('home.postFilter.currentWeight')} ${t('common.minimum')}`}
                    maxAriaLabel={`${t('home.postFilter.currentWeight')} ${t('common.maximum')}`}
                    ticks={weightTicks}
                  />
                </fieldset>

                {/* Wants To Lose */}
                <fieldset className="filter-modal__range">
                  <legend>{t('home.postFilter.wantsToLose')}</legend>
                  <div className="filter-modal__unit-toggle">
                    {(['kg', 'lbs'] as const).map(u => (
                      <button
                        key={u}
                        type="button"
                        className={`filter-modal__unit${loseUnit === u ? ' active' : ''}`}
                        onClick={() => setLoseUnit(u)}
                        aria-pressed={loseUnit === u}
                      >
                        {t(u === 'kg' ? 'basicInfo.unitShortKg' : 'basicInfo.unitShortLbs')}
                      </button>
                    ))}
                  </div>
                  <ScaleBar
                    min={-1}
                    max={loseConfig.max}
                    value={wantsToLoseDisplay}
                    onChange={handleLoseChange}
                    formatValue={v => (v === -1 ? t('buddiesSearch.any') : `${v} ${loseUnit}`)}
                    ariaLabel={t('home.postFilter.wantsToLose')}
                    ticks={[t('buddiesSearch.any'), ...loseTicks]}
                  />
                </fieldset>

                {/* Country */}
                <fieldset>
                  <legend>{t('home.postFilter.country')}</legend>
                  <select
                    className="filter-modal__select"
                    value={String(country ?? 0)}
                    onChange={e => setCountry(Number(e.target.value) || undefined)}
                  >
                    <option value={0}>{t('home.postFilter.allCountries')}</option>
                    {COUNTRIES.map(c => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </fieldset>
              </>
            )}

            {/* Following — always visible, disabled when seeAll or followers */}
            <label className="filter-modal__toggle">
              <span>
                <strong>{t('home.postFilter.following')}</strong>
                <small>{t('home.postFilter.followingDesc')}</small>
              </span>
              <input
                type="checkbox"
                checked={following}
                onChange={e => handleFollowing(e.target.checked)}
                disabled={seeAll || followers}
              />
            </label>

            {/* Followers — always visible, disabled when seeAll or following */}
            <label className="filter-modal__toggle">
              <span>
                <strong>{t('home.postFilter.followers')}</strong>
                <small>{t('home.postFilter.followersDesc')}</small>
              </span>
              <input
                type="checkbox"
                checked={followers}
                onChange={e => handleFollowers(e.target.checked)}
                disabled={seeAll || following}
              />
            </label>

            {/* Clear + Apply */}
            <div className="filter-modal__footer">
              <Button variant="outline" onClick={() => { handleClearPeople().catch(() => undefined); }} disabled={isApplying || isClearing}>{t('home.postFilter.clearFilter')}</Button>
              <Button onClick={() => { handleApplyPeople().catch(() => undefined); }} disabled={isApplying || isClearing}>{t('home.postFilter.applyFilter')}</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
